import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ApiFollowedShow, ApiShow, ApiTopic, ApiUser, FollowApi } from '@tytapp/api';
import { BillingService } from '@tytapp/billing';
import { AppConfig, FormInput, HostApi, IFrameResizer, PageComponent, sleep } from '@tytapp/common';
import { PageApiService } from '@tytapp/common-ui';
import { environment } from '@tytapp/environment';
import { isClientSide, isOnline } from '@tytapp/environment-utils';
import { ShowsService } from '@tytapp/shows';
import { TopicsService } from '@tytapp/topics';
import { AudienceFilter, UserService } from '@tytapp/user';

export interface SettingsSection {
    id: string;
    label: string;
    hidden?: boolean;
    icon?: string;
    externalUrl?: string;
    openInNewTab?: boolean;
    defaultSubPage?: string;
    requiresAnyCapability?: string[];
    requiresAllCapabilities?: string[];
    requiresAccountState?: 'no-account' | 'has-account';
    action?: (event: MouseEvent) => void;
    audiences?: AudienceFilter;
    subpages?: SettingsSection[];
    doNotShowTopHeading?: boolean;
}

export interface InfoSection {
    label: string;
    items: InfoItem[];
}
export interface InfoItem {
    label: string;
    value: string;
}

@Component({
    selector: 'tyt-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss']
})
export class SettingsComponent extends PageComponent {
    private userService = inject(UserService);
    private topicsService = inject(TopicsService);
    private showsService = inject(ShowsService);
    private followApi = inject(FollowApi);
    private domSanitizer = inject(DomSanitizer);
    private pageApiService = inject(PageApiService);
    private hostApi = inject(HostApi);
    private matSnackBar = inject(MatSnackBar);
    private billingService = inject(BillingService);

    get isOnline() {
        return isOnline();
    }

    get shellVersion() {
        return this.hostApi.capabilitiesMessage?.version;
    }

    get shellBuildType() {
        return this.hostApi.capabilitiesMessage?.build_type;
    }

    get shellBundleIdentifier() {
        return this.hostApi.capabilitiesMessage?.bundle_identifier;
    }

    get shellPlatform() {
        return this.hostApi.capabilitiesMessage?.platform;
    }

    shouldShowSection(section: SettingsSection) {
        if (section.requiresAccountState) {
            let state = this.user ? 'has-account' as const : 'no-account' as const;
            if (section.requiresAccountState !== state)
                return false;
        }

        if (section.audiences && !this.userService.matchesAudience(section.audiences))
            return false;

        if (section.requiresAnyCapability) {
            if (!section.requiresAnyCapability.some(n => this.hostApi.capabilities.includes(n))) {
                return false;
            }
        }
        if (section.requiresAllCapabilities) {
            if (!section.requiresAllCapabilities.every(n => this.hostApi.capabilities.includes(n))) {
                return false;
            }
        }
        return !section.hidden || this.mainSectionId === section.id;
    }

    sections: SettingsSection[] = [];

    private disableRedirect: boolean = false;

    valuePropReason = "Stand with the only news network that stands **with you.**";
    reason = "Follow Your Favorites. Sign up for **free**";

    @FormInput()
    newsletter = false;

    membershipAccountUrl: SafeHtml;
    membershipSettingsUrl: SafeHtml;
    oneTimeContributionUrl: SafeHtml;

    user: ApiUser;
    identity: ApiUser;
    showTopics: ApiTopic[];
    sectionReady: boolean = false;
    followedShows: string[];
    topics: ApiTopic[];
    categoryTopics: ApiTopic[];
    hostTopics: ApiTopic[];
    candidateTopics: ApiTopic[];
    politicianTopics: ApiTopic[];

    followedCategoryTopics: string[];
    followedHostTopics: string[];
    followedPoliticianTopics: string[];
    followedShowTopics: string[];
    followedCandidateTopics: string[];

    async loadTopics() {
        if (isOnline()) {
            this.topics = await this.topicsService.allWithLegacyTopics();
            this.categoryTopics = this.getTopicsOfType('topic');
            this.hostTopics = this.getTopicsOfType('host');
            this.candidateTopics = this.getTopicsOfType('candidate');
            this.politicianTopics = this.getTopicsOfType('politician');

            let followedTopics = await this.followApi.getFollows().toPromise();

            this.followedCategoryTopics = followedTopics.filter(x => x.topic_type === 'topic').map(x => x.slug);
            this.followedHostTopics = followedTopics.filter(x => x.topic_type === 'host').map(x => x.slug);
            this.followedPoliticianTopics = followedTopics.filter(x => x.topic_type === 'politician').map(x => x.slug);
            this.followedCandidateTopics = followedTopics.filter(x => x.topic_type === 'candidate').map(x => x.slug);
        }

    }

    isMainSection = false;

    getTopicsOfType(type: string) {
        if (!this.topics)
            return [];

        return this.topics.filter(x => x.topic_type === type);
    }

    afterProfileSave() {
        let returnURL = this.route.snapshot.queryParamMap.get('return');
        if (returnURL)
            this.router.navigateByUrl(returnURL);

    }

    async loadSection() {
        this.sectionReady = false;

        if (['memberships-settings', 'membership-settings', 'membership-accounts', 'plus'].includes(this.sectionId)) {
            this.redirection.go(`/settings/memberships`);
            return;
        }

        if (['notifications', 'newsletters'].includes(this.sectionId)) {
            this.redirection.go(`/settings/messaging`);
            return;
        }

        if (!this.section) {
            this.redirection.go(`/settings`);
            return;
        }

        if (this.section.audiences && !this.user && !this.userService.matchesAudience(this.section.audiences)) {
            this.userService.goToLogin();
        }

        if (this.sectionId === 'one-time-contribution') {
            this.shell.title = 'One Time Contributions';
        } else if (this.sectionId === 'content/shows') {
            this.shell.title = 'Followed Shows';

            if (!this.showTopics && isOnline()) {
                let shows: ApiShow[];
                let followedShows: ApiFollowedShow[];

                let results = await Promise.all([
                    this.showsService.all(),
                    this.followApi.getFollowedShows().toPromise()
                ]);

                shows = results[0] as any;

                let isFollowable = (x: ApiShow) => !!x.is_followable;
                shows = shows.filter(x => isFollowable(x) && !x.is_past_show);

                followedShows = results[1] as any;

                this.showTopics = shows.map(x => <ApiTopic>{
                    id: `show:${x.id}`, slug: x.id, name: x.name, thumbnail_square: x.logo_square
                });
                this.followedShowTopics = followedShows.map(x => x.contentful_show_id);
            }
        } else if (this.sectionId === 'content/topics') {
            this.shell.title = 'Followed Topics';
            this.loadTopics();
        } else if (this.sectionId === 'content/hosts') {
            this.shell.title = 'Followed Hosts';
            this.loadTopics();
        } else if (this.sectionId === 'content/candidates') {
            this.shell.title = 'Followed Candidates';
            this.loadTopics();
        } else if (this.sectionId === 'content/politicians') {
            this.shell.title = 'Followed Politicians';
            this.loadTopics();
        } else if (this.sectionId === 'help/info') {
            this.shell.title = 'App Information';
        }

        this.sectionReady = true;
    }

    savingTopics = false;

    async saveCategoryTopics() {
        this.savingTopics = true;
        await this.saveTopicFollows(this.categoryTopics, this.followedCategoryTopics);
        await sleep(500);
        this.pageApiService.bust('home');
        this.userService.onboarding = false;
        this.savingTopics = false;
        this.router.navigateByUrl('/settings/content/shows');
    }

    async saveShowTopics() {
        this.savingTopics = true;
        //await this.saveShowTopics(this.categoryTopics, this.followedCategoryTopics);
        try {
            await this.followApi.patchFollowedShows({
                shows: <any>this.showTopics.map(x => ({
                    contentful_show_id: x.slug,
                    is_followed: this.followedShowTopics.includes(x.slug)
                }))
            }).toPromise();
        } catch (e) {
            this.logger.error(`Failed while saving show-topics:`)
            this.logger.error(e);
            alert(`An error occurred while saving your followed shows. Please try again.`);
            return;
        }

        await sleep(500);
        this.pageApiService.bust('home');
        this.userService.onboarding = false;
        this.savingTopics = false;
        this.router.navigateByUrl('/settings/content/hosts');
    }

    async saveHostTopics() {
        this.savingTopics = true;
        try {
            await this.saveTopicFollows(this.hostTopics, this.followedHostTopics);
        } catch (e) {
            this.logger.error(`Failed while saving host-topics:`)
            this.logger.error(e);
            alert(`An error occurred while saving your followed hosts. Please try again.`);
            return;
        }

        await sleep(500);
        this.pageApiService.bust('home');
        this.userService.onboarding = false;
        this.savingTopics = false;
        //this.router.navigateByUrl('/settings/content/candidates');
        // this.router.navigateByUrl('/settings/content/politicians');
        this.router.navigateByUrl('/settings/content/done');
    }

    async savePoliticianTopics() {
        this.savingTopics = true;
        await this.saveTopicFollows(this.politicianTopics, this.followedPoliticianTopics);
        await sleep(500);
        this.savingTopics = false;

        this.pageApiService.bust('home');
        this.userService.onboarding = false;
        this.router.navigateByUrl('/settings/content/done');
    }

    async saveCandidateTopics() {
        this.savingTopics = true;
        await this.saveTopicFollows(this.candidateTopics, this.followedCandidateTopics);
        await sleep(500);
        this.pageApiService.bust('home');
        this.userService.onboarding = false;
        this.savingTopics = false;
        this.router.navigateByUrl('/settings/content/done');
    }

    async saveTopicFollows(topics: ApiTopic[], followed: string[]) {
        if (!this.topics)
            return;

        await this.followApi.patchFollows({
            topics: topics.map(topic => (<any>{
                slug: topic.slug,
                is_followed: followed.includes(topic.slug)
            }))
        }).toPromise();
    }

    get section(): SettingsSection {
        let mainSectionId = this.sectionId;
        let secondarySectionId = null;

        if (this.sectionId.includes('/')) {
            mainSectionId = this.sectionId.replace(/\/.*/, '');
            secondarySectionId = this.sectionId.substr(mainSectionId.length);
        }

        return this.sections.find(x => x.id === mainSectionId);
    }

    get mainSectionId(): string {

        let mainSectionId = this.sectionId;
        let secondarySectionId = null;

        if (this.sectionId.includes('/')) {
            mainSectionId = this.sectionId.replace(/\/.*/, '');
            secondarySectionId = this.sectionId.substr(mainSectionId.length);
        }

        return mainSectionId;
    }

    get secondarySectionId(): string {
        let secondarySectionId = null;

        if (this.sectionId.includes('/')) {
            let mainSectionId = this.sectionId.replace(/\/.*/, '');
            secondarySectionId = this.sectionId.substr(mainSectionId.length + 1);
        }

        return secondarySectionId;
    }

    get secondarySection(): SettingsSection {
        let mainSection = this.section;

        if (!mainSection)
            return null;

        return (mainSection.subpages || []).find(x => x.id === this.secondarySectionId);
    }

    sectionId = 'account';

    addSection(afterId: string, section: SettingsSection) {
        if (afterId) {
            let afterIndex = this.sections.findIndex(x => x.id === afterId);
            if (afterIndex < 0)
                this.sections.push(section);
            else
                this.sections.splice(afterIndex + 1, 0, section);
        } else {
            this.sections.push(section);
        }
    }

    @ViewChild('accountsIframe')
    accountsIframeElement: ElementRef<HTMLIFrameElement>;

    ngAfterViewInit() {
    }

    onboarding = false;
    enableYouTubeLogin = false;

    get audienceType() {
        return this.userService.audienceType;
    }

    isTextingSupported = false;

    async init(reasonOrParams: string | Object = null) {
        this.subscribe(this.userService.onboardingChanged, onboarding => this.onboarding = onboarding);

        this.membershipAccountUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.urls.accounts}/account?chromeless=1`);
        this.membershipSettingsUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.urls.accounts}/membership-settings?chromeless=1`);
        this.oneTimeContributionUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.urls.accounts}/settings/one-time-contribution?chromeless=1`);

        this.isTextingSupported = await this.shell.hasFeature('apps.web.enable_texting_prefs');

        let self = this;

        this.sections = [
            {
                id: 'account',
                icon: '<img src="/assets/icons-2019/profile.svg" />',
                label: 'Account',
                requiresAccountState: 'has-account',
                subpages: [
                    {
                        id: 'change-email',
                        label: 'Change Email',
                        hidden: true,
                        icon: '<img src="/assets/icons-2019/profile.svg" />'
                    }, {
                        id: 'change-password',
                        label: 'Change Password',
                        hidden: true,
                        icon: '<img src="/assets/icons-2019/profile.svg" />'
                    }, {
                        id: 'change-username',
                        label: 'Choose Username',
                        hidden: true,
                        icon: '<img src="/assets/icons-2019/profile.svg" />'
                    }
                ]
            }, {
                id: 'profile',
                icon: '<img src="/assets/icons-2019/profile.svg" />',
                label: 'Profile',
                audiences: ['all', '-visitor'],
                externalUrl: `/@/about`
            }, {
                id: 'messaging',
                icon: '<img src="/assets/icons-2019/notifications-settings.svg" />',
                label: 'Messaging',
                audiences: ['all', '-visitor'],
                subpages: []
            }, {
                id: 'memberships',
                icon: '<img src="/assets/icons-2019/plus_membership.svg" />',
                label: 'Plus Memberships',
                subpages: []
            }, {
                id: 'signup',
                icon: '<img src="/assets/icons-2019/profile.svg" />',
                requiresAccountState: 'no-account',
                label: 'Create Account',
                externalUrl: '/signup'
            }, {
                id: 'content',
                icon: '<img src="/assets/icons-2019/content.svg" />',
                label: 'Content',
                defaultSubPage: 'topics',
                audiences: ['all', '-visitor'],
                subpages: [
                    {
                        id: 'topics',
                        label: 'Topics',
                        icon: '<img src="/assets/icons-2019/topics.svg" />'
                    },
                    {
                        id: 'shows',
                        label: 'Shows',
                        icon: '<img src="/assets/icons-2019/shows.svg" />'
                    },
                    {
                        id: 'hosts',
                        label: 'Hosts',
                        icon: '<img src="/assets/icons-2019/hosts.svg" />'
                    },
                    {
                        id: 'politicians',
                        label: 'Politicians',
                        icon: '<img src="/assets/icons-2019/candidates.svg" />',
                        hidden: true
                    },
                    {
                        id: 'done',
                        hidden: true,
                        label: 'Saved',
                        icon: ''
                    },
                    {
                        id: 'candidates',
                        label: 'Candidates',
                        icon: '<mat-icon>ballot</mat-icon>',
                        hidden: true
                    }
                ]
            }, {
                id: 'help',
                icon: '<img src="/assets/icons-2019/contact_support.svg" />',
                label: 'Get Help',
                openInNewTab: true,
                subpages: [
                    {
                        id: 'info',
                        label: 'Account & App Information',
                        icon: '<mat-icon>info</mat-icon>',
                        hidden: true
                    },
                    {
                        id: 'email',
                        label: 'Send us an email',
                        icon: '<mat-icon>email</mat-icon>',
                        hidden: true
                    }
                ]
            }, {
                id: 'terms',
                icon: '<img src="/assets/icons-2019/terms_of_service.svg" />',
                label: 'Terms of Service',
                externalUrl: `/terms`,
            }, {
                id: 'privacy',
                icon: '<img src="/assets/icons-2019/privacy_policy.svg" />',
                label: 'Privacy Policy',
                externalUrl: `/privacy`,
            }, {
                id: 'values',
                icon: '<img src="/assets/icons-2019/privacy_policy.svg" />',
                label: 'TYT Core Values',
                externalUrl: `/values`,
            }, {
                id: 'logout',
                icon: '<img src="/assets/icons-2019/log_out.svg" />',
                audiences: ['all', '-visitor'],
                label: 'Sign Out',
                externalUrl: '/logout',
                action($event) {
                    $event.preventDefault();
                    $event.stopPropagation();
                    self.userService.completeLogout();
                    self.userService.user = null;
                    self.router.navigateByUrl('/home');
                }
            }
        ];

        if (await this.shell.hasFeature('apps.web.enable_privacy_controls') && !this.isUserHome) {
            this.addSection('values', {
                id: 'privacy-controls',
                icon: '<img src="/assets/privacy/privacy_controls.svg" />',
                label: 'Privacy Controls',
                audiences: ['all', '-visitor']
            });
        }

        if (await this.shell.hasFeature('apps.web.enable_in_settings_otc')) {
            this.addSection('memberships', {
                id: 'one-time-contribution',
                icon: '<img src="/assets/one-time-contribution.svg" />',
                requiresAnyCapability: ['web_billing:contribution', 'platform_billing:contribution'],
                label: 'One Time Donations',
                audiences: ['all', '-visitor']
            });
        }

        this.route.paramMap.subscribe(async params => {

            this.isMainSection = !params.has('section');

            let selectedSection: string;

            if (params.get('subsection') && params.get('section')) {
                selectedSection = `${params.get('section')}/${params.get('subsection')}`;
            } else {
                selectedSection = params.get('section');
            }

            await this.userService.ready;

            if (!selectedSection) {
                if (this.userService.user)
                    selectedSection = 'account';
                else
                    selectedSection = 'memberships';
            }

            this.sectionId = selectedSection;
            await this.loadSection();
        });

        this.subscribe(this.userService.userChanged, async user => {
            if (!user || this.disableRedirect)
                return;

            this.user = user;
            await this.updateInfoItems();
        });

        this.subscribe(this.userService.identityChanged, async ident => {
            this.identity = ident;
            await this.updateInfoItems();
        });

        this.pageTitle = 'Settings';
    }

    submitting = false;

    get accountsUrl() {
        return environment.urls.accounts;
    }

    get environment() {
        return environment;
    }

    goTo(url: string) {
        this.router.navigateByUrl(url);
    }

    get isUserHome() {
        return this.user?.username === 'home';
    }

    infoSections: InfoSection[] = [];

    get generatedAccountInfoText() {
        return this.infoSections.map(section => `### ${section.label}\n${section.items.map(i => `${i.label}: ${i.value}`).join(`\r\n`)}`).join(`\n\n`);
    }
    get generatedEmailBody() {
        return (
            `Problem description: \n` +
            `${this.problemDescription || 'Not provided'}\n` +
            `\n` +
            `**Helpful information for support personnel**\n` +
            `${this.generatedAccountInfoText}`
        );
    }

    get generatedEmailLink() {
        return `mailto:support@tytnetwork.com?subject=${encodeURIComponent(this.supportEmailTitle)}&body=${encodeURIComponent(this.generatedEmailBody)}`;
    }

    sendEmail() {
        window.open(this.generatedEmailLink);
    }

    supportEmailTitle: string;
    problemDescription: string;

    get onClientSide() {
        return isClientSide();
    }

    async updateInfoItems() {
        await this.hostApi.capabilitiesReady;

        let shellInfo = {
            label: 'Mobile App'
        }

        this.infoSections = [];
        this.infoSections.push({
            label: 'Account State',
            items: [
                { label: 'Type', value: `${this.identity?.type ?? 'None'}` },
                { label: 'Identity', value: `${this.identity?.uuid ?? 'None'}` },
                { label: 'Email', value: `${this.user?.email ?? 'None'}` },
                { label: 'Entitled', value: `${this.userService.entitled ? 'Yes' : 'No'}`},
                { label: 'Membership', value: `${this.identity?.membership?.uuid ?? 'None/Unattached'}`}
            ]
        });

        if (this.shellVersion) {
            this.infoSections.push({
                label: this.shellBuildType === 'emulated' ? 'Native App (Emulated)' : environment.productName,
                items: [
                    { label: 'Native Build', value: this.shellVersion ?? 'unknown' },
                    { label: 'Build Type', value: this.shellBuildType ?? 'unknown' },
                    { label: 'Platform', value: this.shellPlatform ?? 'unknown' },
                    { label: 'Package', value: this.shellBundleIdentifier ?? 'unknown' },
                    { label: 'Core Version', value: environment.name === 'development' ? 'N/A' : (environment.version ?? 'unknown') },
                    { label: 'Core SKU', value: environment.productSku ?? 'unknown' },
                    { label: 'Core Environment', value: environment.name ?? 'unknown' },
                    { label: 'Core Backend', value: environment.apiOverride ?? 'default' }
                ]
            });
        } else {
            this.infoSections.push({
                label: environment.productName,
                items: [
                    { label: 'Version', value: environment.name === 'development' ? 'N/A' : (environment.version ?? 'unknown') },
                    { label: 'Product SKU', value: environment.productSku ?? 'unknown' },
                    { label: 'Environment', value: environment.name ?? 'unknown' },
                    { label: 'Backend', value: environment.apiOverride ?? 'default' },
                ]
            });
        }

        this.infoSections.push({
            label: `Technical Details`,
            items: [
                { label: 'IAP', value: `${this.billingService.token ?? 'None'}`}
            ]
        });

        this.infoSections.push({
            label: `Device`,
            items: [
                { label: 'User Agent', value: isClientSide() ? String(navigator.userAgent ?? 'Unknown') : 'N/A' },
                { label: 'Pixel Density', value: isClientSide() ? String(window.devicePixelRatio ?? 'Unknown') : 'N/A' },
                { label: 'Platform', value: isClientSide() ? (navigator.platform ?? 'Unknown') : 'SSR' }
            ]
        })
    }

    get supportEmailValid() {
        return !!this.problemDescription && !!this.supportEmailTitle;
    }

    copyAccountInfoToClipboard() {
        navigator.clipboard.writeText(this.generatedAccountInfoText);
        this.matSnackBar.open(`Copied to clipboard.`, undefined, {
            duration: 3000
        });
    }
    copySupportEmail() {
        navigator.clipboard.writeText(this.generatedEmailBody);
        this.matSnackBar.open(`Email copied to clipboard.`, undefined, {
            duration: 3000
        });
    }
}
